import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { listApiKeys, createApiKey, deleteApiKey, copyToClipboard } from "../api";

export function useApiKeys() {
  const [apiKeys, setApiKeys] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);

  const loadApiKeys = useCallback(async () => {
    setIsLoading(true);
    try {
      const keys = await listApiKeys();
      setApiKeys(keys);
    } catch (error) {
      console.error("Failed to load API keys:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApiKeys();
  }, [loadApiKeys]);

  const generateApiKey = useCallback(async () => {
    if (isGenerating) return;

    setIsGenerating(true);
    try {
      const newApiKey = await createApiKey();
      await copyToClipboard(newApiKey);
      toast.success("New API key has been generated and copied to clipboard");
      await loadApiKeys();
    } catch (error) {
      toast.error("Failed to generate API key");
      console.error("API key generation error:", error);
    } finally {
      setIsGenerating(false);
    }
  }, [isGenerating, loadApiKeys]);

  const removeApiKey = useCallback(
    async (keyToDelete: string) => {
      try {
        await deleteApiKey(keyToDelete);
        toast.success("API key successfully deleted");
        await loadApiKeys();
      } catch (error) {
        toast.error("Failed to delete API key");
        console.error("API key deletion error:", error);
      }
    },
    [loadApiKeys]
  );

  const copyApiKey = useCallback(async (key: string) => {
    try {
      await copyToClipboard(key);
      toast.success("API key copied to clipboard");
    } catch {
      toast.error("Failed to copy API key");
    }
  }, []);

  return {
    apiKeys,
    isLoading,
    isGenerating,
    generateApiKey,
    removeApiKey,
    copyApiKey,
  };
}

